import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getOverlayContentProps } from '#pplx/frontend/aether/internal/floating-ui/getOverlayContentProps.js';
import { useHideWhenDetached } from '#pplx/frontend/aether/internal/floating-ui/useHideWhenDetached.js';
import { useOverlay } from '#pplx/frontend/aether/internal/overlay/useOverlay.js';
import { useCollisionPaddingWithTopInset } from '#pplx/frontend/aether/internal/popover-collision/useCollisionPaddingWithTopInset.js';
import { ScrollArea } from '#pplx/frontend/aether/internal/scroll-area/ScrollArea.js';
import { PopoverPrimitiveArrow } from '#pplx/frontend/aether/primitives/PopoverPrimitive/internal/PopoverPrimitiveArrow.js';
import { useCloseOnScroll } from '#pplx/frontend/aether/primitives/PopoverPrimitive/useCloseOnScroll.js';
import { usePopoverPrimitiveHoverGroup } from '#pplx/frontend/aether/primitives/PopoverPrimitive/usePopoverPrimitiveHoverGroup.js';
import { useActivationOrderedOverlayStacking } from '#pplx/frontend/overlay-stacking/useOverlayStacking.js';
import * as RadixHoverCardPrimitive from '@radix-ui/react-hover-card';
import * as RadixPopoverPrimitive from '@radix-ui/react-popover';
import { useCallback, useMemo, useRef } from 'react';
const FLOATING_CONTENT_SELECTOR = '[role="menu"], [role="dialog"], [role="tooltip"], [role="listbox"]';
/**
 * Temporary bridge for independently portalled Radix layers. Follow the
 * `aria-controls` chain so interaction with a descendant floating layer does
 * not dismiss its ancestor. Remove this once nested overlays share native
 * cross-portal dismissal coordination.
 */
function isFromPortalledDescendant(event, content) {
    const target = event.detail.originalEvent.target;
    if (!(target instanceof Element) || content == null)
        return false;
    const floatingContent = target.closest(FLOATING_CONTENT_SELECTOR);
    if (floatingContent?.id == null || floatingContent.id.length === 0)
        return false;
    const pendingContentIds = [floatingContent.id];
    const visitedContentIds = new Set();
    const controllingElements = document.querySelectorAll('[aria-controls]');
    while (pendingContentIds.length > 0) {
        const contentId = pendingContentIds.pop();
        if (contentId == null || visitedContentIds.has(contentId))
            continue;
        visitedContentIds.add(contentId);
        for (const controllingElement of controllingElements) {
            if (controllingElement.getAttribute('aria-controls') !== contentId)
                continue;
            if (content.contains(controllingElement))
                return true;
            const parentFloatingContent = controllingElement.closest(FLOATING_CONTENT_SELECTOR);
            if (parentFloatingContent?.id)
                pendingContentIds.push(parentFloatingContent.id);
        }
    }
    return false;
}
function getMatchedTriggerWidth({ interaction, matchTriggerWidthInset, }) {
    const triggerWidth = interaction === 'hover'
        ? 'var(--radix-hover-card-trigger-width)'
        : 'var(--radix-popover-trigger-width)';
    return matchTriggerWidthInset == null
        ? triggerWidth
        : `calc(${triggerWidth} - ${matchTriggerWidthInset})`;
}
function getContentWidthStyle({ interaction, anchorRef, matchTriggerWidth, matchTriggerWidthInset, minWidthPx, maxWidthPx, }) {
    const matchedWidth = matchTriggerWidth === true
        ? getMatchedTriggerWidth({ interaction, matchTriggerWidthInset })
        : undefined;
    if (anchorRef != null && matchedWidth != null) {
        return {
            minWidth: matchedWidth,
            maxWidth: matchedWidth,
        };
    }
    return {
        minWidth: matchedWidth ?? minWidthPx,
        maxWidth: maxWidthPx,
    };
}
function renderContent({ children, shouldUseScrollArea, scrollAreaSlotProps, scrollAreaContentSlotProps, }) {
    if (!shouldUseScrollArea) {
        return children;
    }
    return (_jsx(ScrollArea, { className: scrollAreaSlotProps?.className, maxHeight: scrollAreaSlotProps?.maxHeight, children: _jsx("div", { className: scrollAreaContentSlotProps?.className, style: scrollAreaContentSlotProps?.style, children: children }) }));
}
export function PopoverPrimitive({ triggerElement, children, interaction, open, onOpenChange, side, align, alignOffset, offsetPx, openDelayMs, closeDelayMs, closeOnScroll = true, disabled, anchorRef, matchTriggerWidth, matchTriggerWidthInset, minWidthPx, maxWidthPx, portalContainer, avoidCollisions, collisionBoundary, collisionPadding, onWheelContent, onContentMouseEnter, onContentMouseLeave, onFocusOutside, shouldAutoFocus, autoFocusFirstFocusableElement, shouldShowArrow, contentSlotProps, contentRef, arrowSlotProps, shouldUseScrollArea = true, scrollAreaSlotProps, scrollAreaContentSlotProps, __radixTriggerProps, }) {
    const shouldUseActivationOrder = useActivationOrderedOverlayStacking();
    const { isOpen, handleOpenChange } = useOverlay({ open, onOpenChange });
    const hasAnchorRef = anchorRef != null;
    const virtualAnchorRef = useMemo(() => {
        if (anchorRef == null) {
            return undefined;
        }
        const virtualAnchor = {
            get contextElement() {
                const currentAnchor = anchorRef.current;
                return (currentAnchor?.contextElement ??
                    (currentAnchor instanceof Element ? currentAnchor : undefined));
            },
            getBoundingClientRect: () => anchorRef.current?.getBoundingClientRect() ?? DOMRect.fromRect({}),
        };
        return {
            get current() {
                return anchorRef.current == null ? null : virtualAnchor;
            },
        };
    }, [anchorRef]);
    const contentWidthStyle = getContentWidthStyle({
        interaction,
        anchorRef,
        matchTriggerWidth,
        matchTriggerWidthInset,
        minWidthPx,
        maxWidthPx,
    });
    const resolvedCollisionPadding = useCollisionPaddingWithTopInset(collisionPadding, collisionBoundary);
    const contentStyle = {
        ...contentSlotProps?.style,
        ...contentWidthStyle,
    };
    const collisionProps = {
        avoidCollisions: avoidCollisions ?? true,
        collisionBoundary: collisionBoundary ?? undefined,
        collisionPadding: resolvedCollisionPadding,
    };
    const hoverContentRef = useRef(null);
    const hideWhenDetached = useHideWhenDetached();
    const clickContentRef = useRef(null);
    const setHoverContentRef = useCallback((element) => {
        hoverContentRef.current = element;
        if (contentRef)
            contentRef.current = element;
    }, [contentRef]);
    const setClickContentRef = useCallback((element) => {
        clickContentRef.current = element;
        if (contentRef)
            contentRef.current = element;
    }, [contentRef]);
    const { isClosedByScroll } = useCloseOnScroll({
        isOpen: interaction === 'hover' && isOpen,
        enabled: closeOnScroll,
        onOpenChange: handleOpenChange,
        ignoredScrollContainerRef: hoverContentRef,
    });
    const hoverGroup = usePopoverPrimitiveHoverGroup({
        enabled: interaction === 'hover',
        isOpen: isOpen && !isClosedByScroll,
        onOpenChange: handleOpenChange,
    });
    const renderedContent = renderContent({
        children,
        shouldUseScrollArea,
        scrollAreaSlotProps,
        scrollAreaContentSlotProps,
    });
    const handleWheel = useCallback((event) => {
        onWheelContent?.({ deltaX: event.deltaX, deltaY: event.deltaY });
    }, [onWheelContent]);
    const handleOpenAutoFocus = useCallback((event) => {
        event.preventDefault();
        if (event.target instanceof HTMLElement) {
            event.target.querySelector('button, a')?.focus();
        }
    }, []);
    const handlePreventAutoFocus = useCallback((event) => {
        event.preventDefault();
    }, []);
    const handleFocusOutside = useCallback((event) => {
        if (shouldUseActivationOrder && isFromPortalledDescendant(event, clickContentRef.current)) {
            event.preventDefault();
            return;
        }
        onFocusOutside?.(event);
    }, [onFocusOutside, shouldUseActivationOrder]);
    const handlePointerDownOutside = useCallback((event) => {
        if (shouldUseActivationOrder && isFromPortalledDescendant(event, clickContentRef.current)) {
            event.preventDefault();
        }
    }, [shouldUseActivationOrder]);
    if (disabled === true) {
        return triggerElement;
    }
    const popoverArrowProps = shouldShowArrow === true ? { shouldShowArrow, arrowSlotProps } : { shouldShowArrow };
    if (interaction === 'hover') {
        const radixTriggerProps = __radixTriggerProps;
        const overlayContentProps = getOverlayContentProps({
            ref: setHoverContentRef,
            side,
            align,
            alignOffset,
            sideOffset: offsetPx,
            ...collisionProps,
            hideWhenDetached,
            className: contentSlotProps?.className,
            style: contentStyle,
            'data-hover-group-sibling-replacement': hoverGroup.isSiblingReplacementClose
                ? ''
                : undefined,
            'data-hover-group-instant-open': hoverGroup.shouldSkipOpenAnimation ? '' : undefined,
            onWheel: onWheelContent == null ? undefined : handleWheel,
            onPointerEnter: onContentMouseEnter,
            onPointerLeave: onContentMouseLeave,
        }, { containScrollEvents: shouldUseActivationOrder });
        return (_jsxs(RadixHoverCardPrimitive.Root, { open: isOpen && !isClosedByScroll, onOpenChange: hoverGroup.handleOpenChange, openDelay: hoverGroup.shouldSkipOpenDelay ? 0 : openDelayMs, closeDelay: closeDelayMs, children: [_jsx(RadixHoverCardPrimitive.Trigger, { asChild: true, ...radixTriggerProps, children: triggerElement }), _jsx(RadixHoverCardPrimitive.Portal, { container: portalContainer ?? undefined, children: !isClosedByScroll && (_jsxs(RadixHoverCardPrimitive.Content, { ...overlayContentProps, children: [renderedContent, _jsx(PopoverPrimitiveArrow, { interaction: interaction, ...popoverArrowProps })] })) })] }));
    }
    const radixTriggerProps = __radixTriggerProps;
    const overlayContentProps = getOverlayContentProps({
        ref: setClickContentRef,
        side,
        align,
        alignOffset,
        sideOffset: offsetPx,
        ...collisionProps,
        hideWhenDetached,
        onOpenAutoFocus: shouldAutoFocus === false
            ? handlePreventAutoFocus
            : autoFocusFirstFocusableElement === true
                ? handleOpenAutoFocus
                : undefined,
        className: contentSlotProps?.className,
        style: contentStyle,
        onWheel: onWheelContent == null ? undefined : handleWheel,
        onPointerEnter: onContentMouseEnter,
        onPointerLeave: onContentMouseLeave,
        onFocusOutside: shouldUseActivationOrder || onFocusOutside != null ? handleFocusOutside : undefined,
        onPointerDownOutside: shouldUseActivationOrder ? handlePointerDownOutside : undefined,
        onCloseAutoFocus: shouldAutoFocus === false ? handlePreventAutoFocus : undefined,
    }, { containScrollEvents: shouldUseActivationOrder });
    return (_jsxs(RadixPopoverPrimitive.Root, { open: isOpen, onOpenChange: handleOpenChange, children: [_jsx(RadixPopoverPrimitive.Trigger, { asChild: true, ...radixTriggerProps, children: triggerElement }), hasAnchorRef ? (_jsx(RadixPopoverPrimitive.Anchor
            // Radix's type excludes the unresolved null state used for trigger fallback.
            , { 
                // Radix's type excludes the unresolved null state used for trigger fallback.
                virtualRef: virtualAnchorRef })) : null, _jsx(RadixPopoverPrimitive.Portal, { container: portalContainer ?? undefined, children: _jsxs(RadixPopoverPrimitive.Content, { ...overlayContentProps, children: [renderedContent, _jsx(PopoverPrimitiveArrow, { interaction: interaction, ...popoverArrowProps })] }) })] }));
}
//# sourceMappingURL=index.js.map